import axios from 'axios';
import '../config';
import { FLAG_SYSTEM_DISABLED_EVENT, isFlagSystemDisabledError } from '../utils/flagSystemCapability';
import { siteUserRequest } from '../utils/flagSystem';
import { auditDateOrdinal } from '../utils/flagAudit';
import { confirmFlagApplicability, flagApplicabilityValue } from '../utils/flagApplicability';

const config = (params) => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  ...(params ? { params } : {}),
});
const base = (path) => `${global.config.url}${path}`;
const customer = (customerId) => `/customers/${customerId}`;
const location = ({ customerId, locationId }) => `${customer(customerId)}/locations/${locationId}`;
const requireArray = (response) => {
  if (!Array.isArray(response.data)) throw new Error('The server returned an unexpected list response.');
  return response.data;
};
const featureRequest = async (customerId, request) => {
  const token = localStorage.getItem('token');
  try {
    return await request();
  } catch (error) {
    if (isFlagSystemDisabledError(error) && token === localStorage.getItem('token')) {
      window.dispatchEvent(new CustomEvent(FLAG_SYSTEM_DISABLED_EVENT, { detail: { customerId, token } }));
    }
    throw error;
  }
};

const requireCapability = (data, customerId) => {
  if (!data || data.customerId !== customerId || typeof data.enabled !== 'boolean') {
    throw new Error('The server returned an unexpected Flag System capability response.');
  }
  return { customerId: data.customerId, enabled: data.enabled };
};
const requireCustomerId = (customerId) => {
  if (!Number.isSafeInteger(customerId) || customerId <= 0) throw new Error('A valid customer is required.');
};
const requireScope = (scope) => {
  if (!scope || !Number.isSafeInteger(scope.locationId) || scope.locationId <= 0) throw new Error('A valid customer location is required.');
  requireCustomerId(scope.customerId);
};

export const flagSystemCapabilityApi = {
  /** @returns {Promise<import('./types/flagSystem').FlagSystemCapability>} */
  get: async (customerId) => {
    requireCustomerId(customerId);
    const { data } = await axios.get(base(`${customer(customerId)}/flag-system`), config());
    return requireCapability(data, customerId);
  },
  /** @param {number} customerId @param {boolean} enabled
   * @returns {Promise<import('./types/flagSystem').FlagSystemCapability>} */
  update: async (customerId, enabled) => {
    requireCustomerId(customerId);
    if (typeof enabled !== 'boolean') throw new Error('Flag System enabled must be a boolean.');
    const response = await axios.put(base(`${customer(customerId)}/flag-system`), { enabled }, config());
    if (response.status !== 200) throw new Error('The server returned an unexpected Flag System save status.');
    const data = requireCapability(response.data, customerId);
    if (data.enabled !== enabled) {
      throw new Error('The server did not confirm the requested Flag System setting. Reload status before retrying.');
    }
    return data;
  },
};

const saveDefinition = async (customerId, flagId, request) => {
  const scoped = Object.prototype.hasOwnProperty.call(request, 'applyOnEntry') || Object.prototype.hasOwnProperty.call(request, 'applyOnExit');
  if (scoped && !flagApplicabilityValue(request)) throw new Error('At least one valid flag direction is required.');
  const response = await featureRequest(customerId, () => flagId ?
    axios.put(base(`${customer(customerId)}/flags/${flagId}`), request, config()) :
    axios.post(base(`${customer(customerId)}/flags`), request, config()));
  if (scoped) {
    const data = response.data;
    if (!data || !Number.isSafeInteger(data.flagId) || data.flagId <= 0 || (flagId && data.flagId !== flagId)) {
      const error = new Error('The saved definition identity was not confirmed. Reload before repeating the write.');
      error.code = 'FLAG_APPLICABILITY_UNVERIFIED';
      error.savedFlagId = flagId || (data && Number.isSafeInteger(data.flagId) && data.flagId > 0 ? data.flagId : undefined);
      throw error;
    }
    confirmFlagApplicability(data, request, flagId || data.flagId);
  }
  return response.data;
};
export const flagDefinitionsApi = {
  /** @returns {Promise<import('./types/flagSystem').FlagDefinition[]>} */
  list: async (customerId) => requireArray(await featureRequest(customerId, () => axios.get(base(`${customer(customerId)}/flags`), config()))),
  /** @returns {Promise<import('./types/flagSystem').FlagDefinition>} */
  get: async (customerId, flagId) => (await featureRequest(customerId, () => axios.get(base(`${customer(customerId)}/flags/${flagId}`), config()))).data,
  /** @param {number} customerId @param {import('./types/flagSystem').FlagDefinitionRequest} request */
  create: async (customerId, request) => saveDefinition(customerId, null, request),
  /** @param {number} customerId @param {number} flagId @param {import('./types/flagSystem').FlagDefinitionRequest} request */
  update: async (customerId, flagId, request) => saveDefinition(customerId, flagId, request),
  disable: async (customerId, flagId, revision) => (await featureRequest(customerId, () => axios.delete(base(`${customer(customerId)}/flags/${flagId}`), config({ revision })))).data,
  /** @param {number} customerId @param {File} file @returns {Promise<import('./types/flagSystem').FlagImportResult>} */
  importCsv: async (customerId, file) => {
    requireCustomerId(customerId);
    if (typeof File === 'undefined' || !(file instanceof File)) throw new Error('Choose a CSV file to import.');
    const formData = new FormData();
    formData.append('file', file);
    return (await featureRequest(customerId, () => axios.post(base(`${customer(customerId)}/flags/import`), formData, config()))).data;
  },
};

const requireCheckInAccessPoint = (point, scope) => {
  if (!point || !Number.isSafeInteger(point.accessPointId) || point.accessPointId <= 0 ||
      point.locationId !== scope.locationId || ![null, 'Entry', 'Exit'].includes(point.direction) ||
      !Number.isSafeInteger(point.revision) || point.revision < 0 ||
      ['tile', 'gate', 'updatedAt'].some(key => point[key] !== null && point[key] !== undefined && typeof point[key] !== 'string')) {
    throw new Error('The server returned an unexpected access-point direction. Reload before checking flags.');
  }
  return point;
};
const requireCheckInResult = (result, scope, memberId, accessPointId) => {
  if (!result || result.customerId !== scope.customerId || result.locationId !== scope.locationId ||
      result.memberId !== memberId || result.accessPointId !== accessPointId || typeof result.checkedAtUtc !== 'string' ||
      (result.accessServiceChecked !== undefined && typeof result.accessServiceChecked !== 'boolean') ||
      (result.accessAllowed !== undefined && result.accessAllowed !== null && typeof result.accessAllowed !== 'boolean') ||
      (result.accessStatus !== null && result.accessStatus !== undefined && typeof result.accessStatus !== 'string') ||
      typeof result.lmsChecked !== 'boolean' || typeof result.checkedIn !== 'boolean' || typeof result.status !== 'string') {
    throw new Error('The server returned an unsupported flags-check result. No check-in was completed.');
  }
  if (result.status === 'AccessDenied') {
    if (result.accessServiceChecked !== true || result.accessAllowed !== false) {
      throw new Error('The server returned an inconsistent access decision. No check-in was completed.');
    }
    return result;
  }
  if (typeof result.flagsAllowed !== 'boolean' || !result.flags ||
      result.flags.accessPointId !== accessPointId || !['Entry', 'Exit'].includes(result.flags.direction) ||
      typeof result.flags.allowed !== 'boolean' || !Array.isArray(result.flags.evaluatedFlags) ||
      !Array.isArray(result.flags.triggeredFlags) || !Array.isArray(result.flags.notApplicableFlagIds) ||
      result.flags.allowed !== result.flagsAllowed) {
    throw new Error('The server returned an unsupported flags-check result. No check-in was completed.');
  }
  if ((result.status === 'FlagsBlocked' && (result.flagsAllowed || result.accessServiceChecked === true || result.accessAllowed === true)) ||
      (result.status === 'AccessAllowed' && (!result.flagsAllowed || !result.accessServiceChecked || result.accessAllowed !== true))) {
    throw new Error('The server returned an inconsistent access decision. No check-in was completed.');
  }
  return result;
};
export const flagCheckInApi = {
  accessPoints: async (scope) => {
    requireScope(scope);
    const points = requireArray(await featureRequest(scope.customerId, () =>
      axios.get(base(`${location(scope)}/access-point-directions`), config())));
    const ids = new Set();
    points.forEach(point => {
      requireCheckInAccessPoint(point, scope);
      if (ids.has(point.accessPointId)) throw new Error('The server returned duplicate access points. Reload before checking flags.');
      ids.add(point.accessPointId);
    });
    return points;
  },
  checkFlags: async (scope, memberId, accessPointId) => {
    requireScope(scope);
    if (typeof memberId !== 'string' || !memberId.trim() || memberId.trim().length > 200 ||
        !Number.isSafeInteger(accessPointId) || accessPointId <= 0) throw new Error('Choose a valid member and configured access point.');
    const normalizedMemberId = memberId.trim();
    const { data } = await featureRequest(scope.customerId, () => axios.post(base(`${location(scope)}/check-in`),
      { memberId: normalizedMemberId, accessPointId }, config()));
    return requireCheckInResult(data, scope, normalizedMemberId, accessPointId);
  },
};
export const flagCheckHistoryApi = {
  list: async (scope, accessPointId, page = 0, pageSize = 100) => {
    requireScope(scope);
    if (!Number.isSafeInteger(accessPointId) || accessPointId <= 0 ||
        !Number.isSafeInteger(page) || page < 0 || !Number.isSafeInteger(pageSize) || pageSize < 1 || pageSize > 100) {
      throw new Error('Choose a valid access point and page size.');
    }
    const { data } = await featureRequest(scope.customerId, () => axios.get(
      base(`${location(scope)}/flag-checks`), config({ accessPointId, page, pageSize })));
    if (!data || !Array.isArray(data.items) || data.page !== page || data.pageSize !== pageSize ||
        !Number.isSafeInteger(data.totalCount) || data.totalCount < 0) {
      throw new Error('The server returned an unexpected flag-check history response.');
    }
    return data;
  },
};

const requireAudit = (audit, customerId) => {
  if (!audit || audit.customerId !== customerId ||
      !((typeof audit.id === 'string' && audit.id.trim()) || (Number.isSafeInteger(audit.id) && audit.id > 0)) ||
      typeof audit.occurredAt !== 'string' || typeof audit.action !== 'string' ||
      ['memberId', 'actionLabel', 'actorName', 'userId', 'role', 'roleLabel', 'flagName', 'locationName', 'comment']
        .some(key => audit[key] !== null && audit[key] !== undefined && typeof audit[key] !== 'string') ||
      ['flagId', 'locationId'].some(key => audit[key] !== null && audit[key] !== undefined && (!Number.isSafeInteger(audit[key]) || audit[key] <= 0))) {
    throw new Error('The server returned an unexpected customer audit record.');
  }
  return audit;
};
export const flagAuditsApi = {
  /** @returns {Promise<import('./types/flagSystem').FlagAuditPage>} */
  list: async (customerId, query = {}) => {
    requireCustomerId(customerId);
    const params = { page: query.page === undefined ? 0 : query.page, pageSize: query.pageSize === undefined ? 50 : query.pageSize };
    if (!Number.isSafeInteger(params.page) || params.page < 0 ||
        !Number.isSafeInteger(params.pageSize) || params.pageSize < 1 || params.pageSize > 200) {
      throw new Error('Choose a valid audit page and a page size of at most 200.');
    }
    for (const key of ['fromDate', 'toDate', 'flagId', 'locationId', 'memberId', 'action', 'userId']) {
      if (query[key] !== undefined && query[key] !== null && query[key] !== '') params[key] = query[key];
    }
    if (['fromDate', 'toDate'].some(key => params[key] !== undefined && auditDateOrdinal(params[key]) === null) ||
        ['flagId', 'locationId'].some(key => params[key] !== undefined && (!Number.isSafeInteger(params[key]) || params[key] <= 0)) ||
        ['memberId', 'action', 'userId'].some(key => params[key] !== undefined && typeof params[key] !== 'string')) {
      throw new Error('Choose valid audit dates, identifiers and exact text filters.');
    }
    if (params.fromDate && params.toDate &&
        (auditDateOrdinal(params.toDate) < auditDateOrdinal(params.fromDate) || auditDateOrdinal(params.toDate) - auditDateOrdinal(params.fromDate) + 1 > 366)) {
      throw new Error('Choose an ordered audit date range of at most 366 calendar days.');
    }
    const { data } = await featureRequest(customerId, () => axios.get(base(`${customer(customerId)}/flag-audits`), config(params)));
    if (!data || !Array.isArray(data.items) || data.page !== params.page || data.pageSize !== params.pageSize ||
        !Number.isSafeInteger(data.totalCount) || data.totalCount < 0 || !Number.isSafeInteger(data.totalPages) || data.totalPages < 0 ||
        !data.actionOptions || typeof data.actionOptions !== 'object' || Array.isArray(data.actionOptions) ||
        Object.values(data.actionOptions).some(value => typeof value !== 'string')) {
      throw new Error('The server returned an unexpected audit page response.');
    }
    data.items.forEach(audit => requireAudit(audit, customerId));
    return data;
  },
  /** @returns {Promise<import('./types/flagSystem').FlagAuditDetails>} */
  get: async (customerId, auditId) => {
    requireCustomerId(customerId);
    if (!((typeof auditId === 'string' && auditId.trim()) || (Number.isSafeInteger(auditId) && auditId > 0))) {
      throw new Error('A valid audit record is required.');
    }
    const { data } = await featureRequest(customerId, () => axios.get(
      base(`${customer(customerId)}/flag-audits/${encodeURIComponent(auditId)}`), config()));
    if (!data || typeof data.changeDetailsAvailable !== 'boolean' ||
        (data.changeDetailsWarning !== null && data.changeDetailsWarning !== undefined && typeof data.changeDetailsWarning !== 'string')) {
      throw new Error('The server returned an unexpected audit details response.');
    }
    requireAudit(data.audit, customerId);
    if (String(data.audit.id) !== String(auditId)) throw new Error('The server returned a different audit record.');
    return data;
  },
};

export const memberFlagsApi = {
  /** Customer responses are full definitions; site responses are restricted SiteFlag DTOs. */
  catalog: async (scope) => requireArray(await featureRequest(scope.customerId, () => axios.get(base(`${location(scope)}/flags`), config()))),
  list: async (scope, memberId) => requireArray(await featureRequest(scope.customerId, () => axios.get(base(`${location(scope)}/member-flags`), config({ memberId })))),
  assign: async (scope, request) => (await featureRequest(scope.customerId, () => axios.post(base(`${location(scope)}/member-flags`), request, config()))).data,
  remove: async (scope, memberFlagId, comment) => (await featureRequest(scope.customerId, () => axios.post(base(`${location(scope)}/member-flags/${memberFlagId}/remove`), { comment }, config()))).data,
  /** @returns {Promise<import('./types/flagSystem').FlagMemberMembershipNumberChange>} */
  changeMembershipNumber: async (scope, currentMemberId, newMemberId, comment) => {
    requireScope(scope);
    if ([currentMemberId, newMemberId].some(memberId => typeof memberId !== 'string' || !memberId.trim() || memberId.trim().length > 200) ||
        (comment !== null && typeof comment !== 'string')) throw new Error('Enter valid flag-user membership numbers and an optional comment.');
    const request = { currentMemberId: currentMemberId.trim(), newMemberId: newMemberId.trim(), comment: comment && comment.trim() ? comment.trim() : null };
    const { data } = await featureRequest(scope.customerId, () => axios.patch(
      base(`${location(scope)}/flag-members/membership-number`), request, config()));
    if (!data || data.customerId !== scope.customerId || typeof data.memberId !== 'string' || !data.memberId.trim() ||
        typeof data.changed !== 'boolean' || (data.flagMemberId !== undefined && (!Number.isSafeInteger(data.flagMemberId) || data.flagMemberId <= 0)) ||
        (data.previousMemberId !== undefined && typeof data.previousMemberId !== 'string')) {
      throw new Error('The server returned an unexpected flag-user membership number response.');
    }
    return data;
  },
};

const requireAccessPointId = (accessPointId) => {
  if (!Number.isSafeInteger(accessPointId) || accessPointId <= 0) {
    throw new Error('A verified numeric access-point ID is required for overrides.');
  }
};

export const flagOverridesApi = {
  list: async (scope, memberId, accessPointId) => {
    requireAccessPointId(accessPointId);
    return requireArray(await featureRequest(scope.customerId, () => axios.get(base(`${location(scope)}/flag-overrides`), config({ memberId, accessPointId }))));
  },
  create: async (scope, request) => {
    requireAccessPointId(request.accessPointId);
    return (await featureRequest(scope.customerId, () => axios.post(base(`${location(scope)}/flag-overrides`), {
      memberFlagId: request.memberFlagId,
      accessPointId: request.accessPointId,
      comment: request.comment,
    }, config()))).data;
  },
};

export const siteUsersApi = {
  /** @returns {Promise<import('./types/flagSystem').SiteUser[]>} */
  list: async (customerId) => requireArray(await featureRequest(customerId, () => axios.get(base(`${customer(customerId)}/site-users`), config()))),
  /** @returns {Promise<import('./types/flagSystem').SiteUserCreationResponse>} */
  create: async (customerId, request) => {
    requireCustomerId(customerId);
    const response = await featureRequest(customerId, () => axios.post(base(`${customer(customerId)}/site-users`), siteUserRequest(request), config()));
    const data = response.data;
    if (response.status !== 201 || !data || typeof data.id !== 'string' || !data.id.trim() ||
        typeof data.email !== 'string' || !data.email.trim() ||
        data.customerId !== customerId || typeof data.invitationEmailSent !== 'boolean' ||
        typeof data.message !== 'string' || !data.message.trim()) {
      throw Object.assign(new Error('The server did not return the documented site-user creation and invitation result. The account may already exist; reload the user list before trying again.'), { response });
    }
    return data;
  },
  /** @returns {Promise<import('./types/flagSystem').SiteUserInvitationResponse>} */
  resendInvitation: async (customerId, userId) => {
    requireCustomerId(customerId);
    if (typeof userId !== 'string' || !userId.trim()) throw new Error('A valid site user is required.');
    const response = await featureRequest(customerId, () => axios.post(
      base(`${customer(customerId)}/site-users/${encodeURIComponent(userId)}/resend-invitation`), undefined, config()));
    if (response.status !== 200 || !response.data || typeof response.data.message !== 'string' || !response.data.message.trim()) {
      throw new Error('The server did not confirm invitation delivery. Reload the user list and check the invitation configuration before trying again.');
    }
    return { message: response.data.message };
  },
  update: async (customerId, userId, request) => {
    await featureRequest(customerId, () => axios.put(base(`${customer(customerId)}/site-users/${encodeURIComponent(userId)}`), request, config()));
  },
  delete: async (customerId, userId) => {
    await featureRequest(customerId, () => axios.delete(base(`${customer(customerId)}/site-users/${encodeURIComponent(userId)}`), config()));
  },
};

export const flagLookupsApi = {
  locations: async (customerId) => {
    try {
      return requireArray(await axios.get(base(`/Support/locations/${customerId}/false`), config()));
    } catch (error) {
      // The documented empty-location response is a 404, not an authorization failure.
      if (error.response && error.response.status === 404 &&
          error.response.data === 'No locations found for the specified customer.') return [];
      throw error;
    }
  },
  tiles: async (scope) => requireArray(await axios.get(base(`/Support/tiles/sites/location/${scope.locationId}/${scope.customerId}`), config())),
  adminCustomer: async (customerId) => (await axios.get(base(`/Customers/${customerId}`), config())).data,
};

export const flagHistoryApi = {
  list: async (customerId, tileId, query) => {
    try {
      return requireArray(await axios.get(base(`/Support/tileslog/${encodeURIComponent(tileId)}/${customerId}`), config(query)));
    } catch (error) {
      if (error.response && error.response.status === 404) return [];
      throw error;
    }
  },
};
